import {
  Checkbox,
  CheckboxChangeEvent,
  Col,
  DatePicker,
  Divider,
  Input,
  Radio,
  Row,
  Select,
  Typography,
} from "antd";
import arLocale from "antd/es/date-picker/locale/ar_EG";
import enLocale from "antd/es/date-picker/locale/en_GB";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { ChangeEvent, useMemo } from "react";
import { useIntl } from "react-intl";
import { _eventBus } from "../../core/events/EventBus";
import { countries, formatCallingCode } from "../../Lib/Utils/countries";
import { matchesCountryTerm } from "../../Lib/Utils/countryFilter";
import { isEmailField, isValidEmail, isValidMobile } from "../../Lib/Utils/serviceRequestValidation";
import { srReqClaimTree } from "../../Orchestrator/ConstantTree/ServiceRequestClaim/ServiceRequestClaim";
import { useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import { IBookingResponse, IPassenger } from "../../Orchestrator/Types/Index";
import { IPassengerSelection } from "../../Orchestrator/Types/Interface/IPassenger";
import {
  IAdditionalField,
  IModifiedPassengerName,
  ISelectedCategoryAndSubCategory,
} from "../../Orchestrator/Types/Interface/IServiceRequest";
import CustomDropDown from "../CustomDropDown/CustomDropDown";
import { getIconForType } from "../LandingPage/FromDropDown/GetIconForType";
import ServiceRequestModal from "../ServiceRequest/ServiceRequestModal/ServiceRequestModal";
import FlagImage from "../Shared/FlagImage";
import HelpInfoSection from "./HelpInfoSection";
import "./style.css";
import TitleField from "./TitleField";
import "../Feature/Calendar/Calendar.css"
import Image from "next/image";
import GroupBookingPassengerSelector from "../GroupBookingForm/GroupBookingPassengerSelector";

const DynamicForm: React.FC<{ additionalFields?: IAdditionalField[] }> = ({
  additionalFields = [],
}) => {
  const intl = useIntl();
  const [formData] = useRecState<{ [key: string]: string }>(
    "serviceRequestFormDataAtom"
  );
  const [isButtonDisabled] = useRecState<boolean>(
    "submitRequestButtonDisabledAtom"
  );
  const [modifiedPassengerName] = useRecState<IModifiedPassengerName>(
    "modifiedPassengerNameAtom"
  );
  const [booking] = useRecState<IBookingResponse>("bookingResponseAtom");
  const [selectedCategoryAndSubCategory] =
    useRecState<ISelectedCategoryAndSubCategory>(
      "selectedCategoryAndSubCategoryAtom"
    );
  const [passengerTypes] = useRecState<IPassengerSelection[]>(
    "passengerTypesStateAtom"
  );

  const handleChange = (field: string, fieldId: string, value: string) => {
    _eventBus.emit({
      eventName: srReqClaimTree.RequestClaim.handleChange,
      data: {
        additionalFields,
        formData,
        fieldId,
        value,
        fieldTitle: field,
      },
    });
    // const oldFields = { ...formData };
    // oldFields[fieldId] = value;

    // //Check filed which is affect to the this current filed change.
    // const filedConditions = additionalFields.filter((f) =>
    //   (f?.conditions || []).find((c) => c.when === field)
    // );
    // if (filedConditions.length > 0) {
    //   filedConditions.forEach((filedCondition) => {
    //     if (filedCondition?.conditions) {
    //       const condition = filedCondition.conditions.every(
    //         (condition) => oldFields[fieldId] === condition.match
    //       );
    //       if (!condition) {
    //         delete oldFields[filedCondition.fieldId];
    //       }
    //     }
    //   });
    // }

    // setFormData({ ...oldFields });
  };

  /**
   * Can't create action for shouldRenderField function.
   * Because it's return a instant value and event does not have callback.
   * */
  const shouldRenderField = (field: IAdditionalField) => {
    // _eventBus.emit({
    //   eventName: srReqClaimTree.RequestClaim.ShouldRenderFiled,
    //   data: {},
    // });
    if (!field.conditions) return true;
    return field.conditions.every((condition) => {
      const conditionWhenFiledId = additionalFields.find(
        (conFiled) => conFiled.title === condition.when
      )?.fieldId;
      if (conditionWhenFiledId) {
        return formData[conditionWhenFiledId] === condition.match;
      }
      return false;
    });
  };

  const saveServiceRequest = async () => {
    _eventBus.emit({
      eventName: srReqClaimTree.RequestClaim.Save,
      data: {
        additionalFields,
        formData,
        selectedCategoryAndSubCategory,
        bookingId: booking?.booking?.id || "",
      },
    });
  };

  const getEmailError = (field: IAdditionalField): string | null => {
    const value = formData[field.fieldId];
    if (!value || !value.trim()) return null;
    if (!isValidEmail(value)) return intl.formatMessage({ id: "invalidEmail" });
    return null;
  };

  const getMobileError = (field: IAdditionalField): string | null => {
    const value = formData[field.fieldId];
    if (!value || !value.trim()) return null;
    const countryCode = formData["mobileCountryCode"] || "+971";
    if (!isValidMobile(value, countryCode)) return intl.formatMessage({ id: "invalidMobile" });
    return null;
  };

  const getFieldWidth = (field: IAdditionalField) => {
    let width = 24; // 24 means 100%
    if (field?.width && field?.width === "50%") width = 12;

    //else if added for Invoice Related fields need to set width as 12 as per FIGMA (If field is coming from Backend then it is setting from that only)
    else if ((field?.type == "TEXT" && (field?.title == "COMPANY_NAME" || field?.title == "TRN" || field?.title == "ADDRESS_LINE_1" || field?.title == "ADDRESS_LINE_2" || field?.title == "CITY" || field?.title == "EMAIL")) || (field?.type == "COUNTRY" && field?.title == "COUNTRY") || field?.type == "MOBILE" || field?.type == "STATION" || field?.type == "DATE" || field?.type == "PASSENGER_TYPE" || field?.title == "PASSENGER_TYPE" || field?.title == "PROFILE") width = 12;
    return width;
  };

  const onPassengerCheckBoxChange = (
    event: CheckboxChangeEvent,
    passengerId: string
  ) => {
    _eventBus.emit({
      eventName: srReqClaimTree.RequestClaim.OnPassengerCheckBoxChange,
      data: {
        event,
        passengerId,
        type: "CHECKBOX_CHANGE",
      },
    });
  };
  const onChangePassengerNameInput = (
    event: ChangeEvent<HTMLInputElement>,
    passengerId: string,
    key: "firstName" | "lastName"
  ) => {
    _eventBus.emit({
      eventName: srReqClaimTree.RequestClaim.OnPassengerCheckBoxChange,
      data: {
        event,
        passengerId,
        type: "ONCHANGE_PASSENGER_NAME",
        key,
      },
    });
  };
  const getFieldOrder = (field: IAdditionalField): number => {
    const title = field.title || "";
    const type = field.type || "";
    if (type === "HELP_INFO" || type === "INFO_BOX") return 0;
    if (title.includes("FIRST_NAME")) return 1;
    if (title.includes("LAST_NAME")) return 2;
    if (title === "EMAIL") return 3;
    if (type === "MOBILE") return 4;
    if (type === "STATION" || type === "SELECT") return 5;
    if (type === "DATE") return 6;
    if (type === "PASSENGER_TYPE" || title === "PASSENGER_TYPE" || title === "PROFILE") return 7;
    if (type === "TEXT" && field.maxLines) return 8;
    return 9;
  };

  const sortedFields = useMemo(() => {
    return [...additionalFields].sort((a, b) => getFieldOrder(a) - getFieldOrder(b));
  }, [additionalFields]);

  /**
   * Can't create action for renderFields function.
   * Because it's return a HTML part.
   * */
  const renderFields = useMemo(() => {
    return sortedFields.map((field: IAdditionalField, index: number) => {
      if (!shouldRenderField(field)) return null;
      let stopName: string = "";
      if (field.title === "PICK_UP_POINT_ORIGIN" && booking?.booking?.id) {
        stopName = intl.formatMessage({
          id:
            booking?.booking?.trips[0]?.legs[0]?.timedLeg?.start?.stopPlaceRef
              .stopPlaceRef || "",
        });
      }
      if (field.title === "PICK_UP_POINT_DESTINATION" && booking?.booking?.id) {
        stopName = intl.formatMessage({
          id:
            booking?.booking?.trips[0]?.legs[0]?.timedLeg?.end?.stopPlaceRef
              .stopPlaceRef || "",
        });
      }
      // Check if this field should be rendered as PASSENGER_TYPE selector
      const isPassengerTypeField = field.type === "PASSENGER_TYPE" ||
                                   field.title === "PASSENGER_TYPE" ||
                                   field.title === "PROFILE";

      // Render PASSENGER_TYPE selector for passenger type fields
      if (isPassengerTypeField) {
        // Override field title to show "NUMBER_OF_PASSENGERS" label
        const passengerTypeFieldWithLabel = {
          ...field,
          title: "NUMBER_OF_PASSENGERS",
        };

        return (
          <Col span={getFieldWidth(field)} key={index}>
            <Row className="p-10">
              <TitleField field={passengerTypeFieldWithLabel} />
              <Col span={24}>
                <GroupBookingPassengerSelector
                  fieldId={field.fieldId}
                  value={formData[field.fieldId] || ""}
                  minPassenger={field.min || 10}
                  onChange={(value: string) =>
                    handleChange(field.title, field.fieldId, value)
                  }
                  passengerTypes={passengerTypes || []}
                />
              </Col>
            </Row>
          </Col>
        );
      }

      switch (field.type) {
        case "INFO_BOX":
          return (
            <Col span={getFieldWidth(field)} key={index}>
              <Row className="p-10 filed-info mb-10">
                {field?.icon && (
                  <Col
                    span={2}
                    className="align-items-center justify-content-center"
                  >
                    {getIconForType(field.icon)}
                  </Col>
                )}
                <Col span={22}>
                  <Typography.Text className="fs-16 fw-600">
                    {intl.formatMessage({ id: field.title })}
                  </Typography.Text>
                </Col>
              </Row>
            </Col>
          );
        case "TEXT": {
          // Hide NUMBER_OF_PASSENGERS field - it will be auto-populated from PASSENGER_TYPE
          if (field.title === "NUMBER_OF_PASSENGERS") {
            return (
              <input
                key={index}
                type="hidden"
                value={formData[field.fieldId] || ""}
              />
            );
          }
          const emailError = isEmailField(field) ? getEmailError(field) : null;
          return (
            <Col span={getFieldWidth(field)} key={index}>
              <Row className="p-10">
                <TitleField field={field} stopName={stopName} />
                <Col span={24}>
                  {field?.maxLines ? (
                    <TextArea
                      spellCheck={true}
                      placeholder={`${intl.formatMessage({
                        id: "enterText",
                      })} ${intl.formatMessage({ id: field.title })}`}
                      value={formData[field.fieldId] || ""}
                      onChange={(e) => {
                        handleChange(
                          field.title,
                          field.fieldId,
                          e.target.value
                        );
                      }}
                      className="radius-12"
                      rows={4}
                    />
                  ) : (
                    <>
                      <Input
                        placeholder={`${intl.formatMessage({
                          id: "enterText",
                        })} ${intl.formatMessage({ id: field.title })}`}
                        value={formData[field.fieldId] || ""}
                        onChange={(e) => {
                          handleChange(field.title, field.fieldId, e.target.value);
                        }}
                        className="h-50 radius-12"
                        status={emailError ? "error" : undefined}
                      />
                      {emailError && (
                        <span className="text-error fs-12">{emailError}</span>
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </Col>
          );
        }
        case "DATE":
          return (
            <Col span={getFieldWidth(field)} key={index}>
              <Row className="p-10">
                <TitleField field={field} stopName={stopName} />
                <Col span={24}>
                  <DatePicker
                    className="select-date"
                    classNames={{ popup: { root: "personal-info-date change-date-booking" } }}
                    style={{ width: "100%" }}
                    onChange={(date: any) => {
                      handleChange(
                        field.title,
                        field.fieldId,
                        date ? date.format("YYYY-MM-DD") : ""
                      );
                    }}
                    format="DD/MM/YYYY"
                    locale={intl.locale === "en" ? enLocale : arLocale}
                    value={
                      formData[field.fieldId]
                        ? dayjs(formData[field.fieldId])
                        : ""
                    }
                    disabledDate={field.title === "TRAVEL_DATE" ? (current) => current && current < dayjs().startOf('day') : undefined}
                    suffixIcon={
                      <Image
                        src="/assets/search_icons/calendar-empty-alt.svg"
                        width={24}
                        height={24}
                        alt="calendar"
                      />
                    }
                  />
                </Col>
              </Row>
            </Col>
          );
        case "RADIO":
          return (
            <Col span={getFieldWidth(field)} key={index}>
              <Row className="filed-radio p-10">
                <TitleField field={field} stopName={stopName} span={19} />
                <Col span={5}>
                  <Radio.Group
                    className="radio-option"
                    value={formData[field.fieldId] || ""}
                    onChange={(e) =>
                      handleChange(field.title, field.fieldId, e.target.value)
                    }
                  >
                    <Radio value={"true"}>
                      {intl.formatMessage({ id: "yesText" })}
                    </Radio>
                    <Radio value={"false"}>
                      {intl.formatMessage({ id: "noText" })}
                    </Radio>
                  </Radio.Group>
                </Col>
                <Divider className="mb-5" />
              </Row>
            </Col>
          );
        case "SELECT":
          return (
            <Col span={getFieldWidth(field)} key={index}>
              <Row className="p-10">
                <TitleField field={field} stopName={stopName} />
                {field?.values && (
                  <Col span={24}>
                    <CustomDropDown
                      value={formData[field.fieldId] || ""}
                      onChange={(value: string, item: any) =>
                        handleChange(field.title, field.fieldId, value)
                      }
                      Options={field.values.map((value: string) => {
                        return {
                          labelTranslationKey: value,
                          value: value,
                        };
                      })}
                      placeHolderTranslationKey={`${intl.formatMessage({
                        id: "select",
                      })} ${intl.formatMessage({ id: field.title })}`}
                    />
                  </Col>
                )}
              </Row>
            </Col>
          );
        case "HELP_INFO":
          return (
            <Col span={getFieldWidth(field)} key={index}>
              <Row className="p-10 filed-help-info">
                <HelpInfoSection infoType={field?.helpInfoType || ""} />
              </Row>
            </Col>
          );
        case "MOBILE": {
          const mobileError = getMobileError(field);
          return (
            <Col span={getFieldWidth(field)} key={index}>
              <Row className="p-10">
                <TitleField field={field} stopName={stopName} />
                <Col span={24}>
                  <div className="d-flex w-100">
                    <Select
                      value={formData["mobileCountryCode"] || "+971"}
                      onChange={(value) => {
                        handleChange(field.title, "mobileCountryCode", value);
                      }}
                      aria-label="combobox"
                      className="h-50 mr-10"
                      optionLabelProp="label"
                      style={{ width: "160px" }}
                      styles={{ popup: { root: { width: "320px", maxWidth: "360px" } } }}
                      // Built-in showSearch + filterOption: the Select renders
                      // its own search input in the open header, no portal-
                      // focus voodoo required (popupRender + CountrySearchPopup
                      // had keyboard-input issues in this form's context).
                      showSearch
                      placeholder={intl.formatMessage({ id: "searchCountryOrCode" })}
                      filterOption={(input, option) => {
                        if (!input) return true;
                        const country = countries.find(
                          (c) => c.countryCallingCode === option?.value,
                        );
                        return country ? matchesCountryTerm(country, input) : false;
                      }}
                    >
                      {countries.map((country) => (
                        <Select.Option
                          key={country.id}
                          value={country.countryCallingCode}
                          label={
                            <div className="country-option d-flex gap-10 align-items-center">
                              <FlagImage countryCode={country.countryCode} />
                              <span>{formatCallingCode(country.countryCallingCode)}</span>
                            </div>
                          }
                          className="country-code-option"
                        >
                          <div className="country-option d-flex gap-10 align-items-center">
                            <FlagImage countryCode={country.countryCode} />
                            <span>{country.label} {formatCallingCode(country.countryCallingCode)}</span>
                          </div>
                        </Select.Option>
                      ))}
                    </Select>
                    <Input
                      id="mobileContact"
                      placeholder={intl.formatMessage({
                        id: "mobilePlaceholder",
                      })}
                      value={formData[field.fieldId] || ""}
                      onChange={(e) => {
                        handleChange(
                          field.title,
                          field.fieldId,
                          e.target.value
                        );
                      }}
                      className="h-50 w-100"
                      status={mobileError ? "error" : undefined}
                    />
                  </div>
                  {mobileError && (
                    <span className="text-error fs-12">{mobileError}</span>
                  )}
                </Col>
              </Row>
            </Col>
          );
        }
        case "COUNTRY":
          return (
            <Col span={getFieldWidth(field)} key={index}>
              <Row className="p-10">
                <TitleField field={field} stopName={stopName} />
                <Col span={24}>
                  <Select
                    value={formData[field.fieldId] || "AE"}
                    onChange={(value) => {
                      handleChange(field.title, field.fieldId, value);
                    }}
                    aria-label="combobox"
                    className="h-50 w-100 dynamic-select-country"
                    placeholder={`${intl.formatMessage({
                      id: "select",
                    })} ${intl.formatMessage({ id: field.title })}`}
                  >
                    {countries.map((country) => (
                      <Select.Option
                        key={country.id}
                        value={country.countryCode}
                        label={
                          <div className="country-option d-flex gap-10 align-items-center">
                            <FlagImage countryCode={country.countryCode} />
                            <span>{country.label}</span>
                          </div>
                        }
                        className="country-code-option"
                      >
                        <div className="country-option d-flex gap-10 align-items-center">
                          <FlagImage countryCode={country.countryCode} />
                          <span>{country.label}</span>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            </Col>
          );
        case "STATION":
          return (
            <Col span={getFieldWidth(field)} key={index}>
              <Row className="p-10">
                <TitleField field={field} />
                {field?.values && (
                  <Col span={24}>
                    <CustomDropDown
                      value={formData[field.fieldId] || ""}
                      onChange={(value: string, item: any) =>
                        handleChange(field.title, field.fieldId, value)
                      }
                      Options={field.values.map((value: string) => {
                        return {
                          labelTranslationKey: value,
                          value: value,
                        };
                      })}
                      placeHolderTranslationKey={`${intl.formatMessage({
                        id: "select",
                      })} ${intl.formatMessage({ id: field.title })}`}
                      labelMode={field?.type === "STATION" ? "stopName" : "translationKey"}
                    />
                  </Col>
                )}
              </Row>
            </Col>
          );
        case "PASSENGER_NAME":
          return (
            <Col span={getFieldWidth(field)} key={index}>
              <Row className="p-10">
                <TitleField field={field} />
                <Col span={24}>
                  <Input
                    placeholder={`${intl.formatMessage({
                      id: "enterText",
                    })} ${intl.formatMessage({ id: field.title })}`}
                    value={formData[field.fieldId] || ""}
                    onChange={(e) => {
                      handleChange(field.title, field.fieldId, e.target.value);
                    }}
                    className="h-50 radius-12"
                  />
                </Col>
              </Row>
            </Col>
          );
        case "PASSENGER_NAME_CHANGE":
          return (
            <Col style={booking?.booking?.passengers?.length > 0 ? {} : { minHeight: '180px' }} span={getFieldWidth(field)} key={index}>
              <Row className="p-10 field-passenger-name">
                <Col span={24} className="p-10">
                  {booking?.booking?.passengers?.map(
                    (passenger: IPassenger, index: number) => {
                      const passengerId = `${passenger.id}`;
                      return (
                        <Row
                          key={index}
                          gutter={[16, 16]}
                          className="w-100 mb-10 p-10 passenger-list"
                          style={{ minHeight: "72px" }}
                        >
                          <Col
                            span={modifiedPassengerName[passengerId] ? 8 : 24}
                            className="align-items-center"
                          >
                            <Checkbox
                              onChange={(e) =>
                                onPassengerCheckBoxChange(e, passengerId)
                              }
                              className="mr-10"
                              id={passengerId}
                            />
                            <label
                              htmlFor={passengerId}
                              className="cursor-pointer"
                            >
                              <Typography.Text className="fs-16 fw-400">
                                {passenger.detail.firstName}{" "}
                                {passenger.detail.lastName}
                              </Typography.Text>
                            </label>
                          </Col>
                          {modifiedPassengerName[passengerId] && (
                            <>
                              <Col span={8}>
                                <Input
                                  id="firstNameContact"
                                  placeholder={intl.formatMessage({
                                    id: "newFirstNamePlaceHolder",
                                  })}
                                  value={
                                    modifiedPassengerName[passengerId]
                                      ?.firstName || ""
                                  }
                                  onChange={(e) =>
                                    onChangePassengerNameInput(
                                      e,
                                      passengerId,
                                      "firstName"
                                    )
                                  }
                                  className="h-50 radius-12"
                                />
                              </Col>
                              <Col span={8}>
                                <Input
                                  id="firstNameContact"
                                  placeholder={intl.formatMessage({
                                    id: "newLastNamePlaceHolder",
                                  })}
                                  value={
                                    modifiedPassengerName[passengerId]
                                      ?.lastName || ""
                                  }
                                  onChange={(e) =>
                                    onChangePassengerNameInput(
                                      e,
                                      passengerId,
                                      "lastName"
                                    )
                                  }
                                  className="h-50 radius-12"
                                />
                              </Col>
                            </>
                          )}
                        </Row>
                      );
                    }
                  )}
                </Col>
              </Row>
            </Col>
          );
        default:
          return <></>;
      }
    });
  }, [sortedFields, formData, booking, intl, modifiedPassengerName, passengerTypes]);

  return (
    <div className="main-dynamic-form">
      <Row>{renderFields}</Row>
      {/* Comment additionalFields.length > 0 as need to have this button even if no additionalFields */}
      {/* {additionalFields.length > 0 && ( */}
      <Row className="p-10 align-items-center">
        <Col span={24}>
          <div className="justify-content-end">
            <button
              className={`${isButtonDisabled ? "disable-btn" : "danger-btn"
                } pr-20 pl-20 h-50`}
              onClick={saveServiceRequest}
              disabled={isButtonDisabled}
            >
              <span className="ml-10 mr-10 fs-16 fw-600">
                {intl.formatMessage({ id: "submitRequestText" })}
              </span>
            </button>
          </div>
        </Col>
      </Row>
      {/* )} */}
      <ServiceRequestModal />
    </div>
  );
};

export default DynamicForm;
